import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useI18n, type Key } from '../i18n';
import { useShopEntry } from '../session';
import { Plate } from '../ui/chrome';
import { AppleGlyph, CheckGlyph } from '../ui/glyphs';
import { COPY, FEATURE_CARD, FEATURE_KEYS, LEAD, MANGA_POINTS, MANGA_PRICE, RESIDENTS, SAMPLE_CHOICE, STEPS, type FeatureKey, type Resident } from './features-content';
import { createPortal } from 'react-dom';

const APP_STORE_URL = import.meta.env.VITE_APP_STORE_URL;
const SAMPLES = [1, 2, 3, 4];

/** The page a landing feature card opens: what the feature is, in more depth. */
export function FeaturePage() {
  const { key } = useParams();
  const { t, lang, tx } = useI18n();
  const shopEntry = useShopEntry();
  if (!FEATURE_KEYS.includes(key as FeatureKey)) return <Navigate to="/" replace />;
  const feature = key as FeatureKey;
  const card = FEATURE_CARD[feature];
  const next = FEATURE_KEYS[(FEATURE_KEYS.indexOf(feature) + 1) % FEATURE_KEYS.length]!;
  return (
    <div className="feature-page">
      <div className="feature-page-bg" aria-hidden />
      <div className="wrap">
        <Link to="/" className="back-link">
          ← {tx(COPY.back)}
        </Link>
        <header className="feature-head">
          <Plate as="h1" large>
            {t(`landing.${card}.title` as Key).replace('\n', lang === 'en' ? ' ' : '')}
          </Plate>
          <p className="feature-lead">{tx(LEAD[feature])}</p>
        </header>
        {feature === 'choices' ? <Choices /> : feature === 'neighbours' ? <Neighbours /> : <Manga />}
        <footer className="feature-foot">
          <div className="hero-actions">
            {APP_STORE_URL ? (
              <a className="store-badge" href={APP_STORE_URL} target="_blank" rel="noreferrer">
                <AppleGlyph />
                <span>
                  <b>App Store</b>
                  <small>{t('landing.appStore')}</small>
                </span>
              </a>
            ) : (
              <span className="store-badge" aria-disabled="true">
                <AppleGlyph />
                <span>
                  <b>App Store</b>
                  <small>{t('landing.appStoreSoon')}</small>
                </span>
              </span>
            )}
            {shopEntry ? (
              <Link className="btn btn-pink" to="/shop">
                {t('landing.toShop')}
              </Link>
            ) : null}
          </div>
          <Link to={`/features/${next}`} className="next-link">
            {tx(COPY.next)}
            {t(`landing.${FEATURE_CARD[next]}.title` as Key).replace('\n', lang === 'en' ? ' ' : '')} →
          </Link>
        </footer>
      </div>
    </div>
  );
}

function Choices() {
  const { tx } = useI18n();
  return (
    <div className="choices">
      <ol className="steps">
        {STEPS.map((s, i) => (
          <li key={i} className="card step-card">
            <span className="step-no">{i + 1}</span>
            <h2>{tx(s.title)}</h2>
            <p>{tx(s.body)}</p>
          </li>
        ))}
      </ol>
      <div className="choice-row">
      <section className="card sample-choice">
        <Plate>{tx(COPY.choiceTitle)}</Plate>
        <div className="sample-choice-body">
          <img src="/art/cast_chloe.webp" alt="" width={120} height={120} />
          <div>
            <p className="speech">
              <b>{tx(SAMPLE_CHOICE.who)}</b>
              {tx(SAMPLE_CHOICE.line)}
            </p>
            <ul className="options">
              {SAMPLE_CHOICE.options.map((o) => (
                <li key={o.en}>▶ {tx(o)}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      <figure className="choice-page">
        <img src="/art/sample_1.webp" alt="" width={900} height={1350} loading="lazy" />
        <figcaption>{tx(COPY.becomes)}</figcaption>
      </figure>
      </div>
    </div>
  );
}

function Neighbours() {
  const { tx } = useI18n();
  return (
    <div className="neighbours">
      <ResidentGroup title={tx(COPY.romance)} people={RESIDENTS.filter((p) => p.romance)} />
      <ResidentGroup title={tx(COPY.locals)} people={RESIDENTS.filter((p) => !p.romance)} />
    </div>
  );
}

function ResidentGroup({ title, people }: { title: string; people: Resident[] }) {
  const { tx } = useI18n();
  return (
    <section className="resident-group">
      <Plate>{title}</Plate>
      <div className="resident-grid">
        {people.map((p) => (
          <Link key={p.id} to={`/neighbours/${p.id}`} className="card resident">
            <div className="resident-scene" style={{ backgroundImage: `url(/art/bg_${p.id}.webp)` }}>
              <img src={`/art/fig_${p.id}.webp`} alt="" loading="lazy" />
            </div>
            <div className="resident-copy">
              <h3>
                {tx(p.name)}
                <small>{tx(COPY.age).replace('{n}', String(p.age))}</small>
              </h3>
              <p className="role">{tx(p.role)}</p>
              <p className="quote">{tx(p.tagline)}</p>
              <span className="resident-more">{tx(COPY.profile)} →</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function Manga() {
  const { tx } = useI18n();
  const [open, setOpen] = useState<number | null>(null);
  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <div className="manga">
      <ul className="manga-points">
        {MANGA_POINTS.map((m) => (
          <li key={m.title.en} className="card">
            <h2>
              <CheckGlyph size={20} />
              {tx(m.title)}
            </h2>
            <p>{tx(m.body)}</p>
          </li>
        ))}
      </ul>
      <p className="notice manga-price">{tx(MANGA_PRICE)}</p>
      <section>
        <Plate>{tx(COPY.samples)}</Plate>
        <p className="muted samples-note">{tx(COPY.samplesNote)}</p>
        <div className="sample-pages">
          {SAMPLES.map((n) => (
            <button key={n} className="sample-page" onClick={() => setOpen(n)}>
              <img src={`/art/sample_${n}.webp`} alt="" width={900} height={1350} loading="lazy" />
            </button>
          ))}
        </div>
      </section>
      {/* On the body, so it covers the nav too (the page's own stacking context would keep it under). */}
      {open !== null
        ? createPortal(
            <div className="lightbox" role="dialog" aria-modal="true" onClick={() => setOpen(null)}>
              <img src={`/art/sample_${open}.webp`} alt="" />
              <button className="lightbox-close" aria-label={tx(COPY.close)}>
                ×
              </button>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
